import { Areas, Design, InterAreaConnection, Segment } from '@thive/fddm-types';
import { Vector2 } from 'three';
import { RAD2DEG } from 'three/src/math/MathUtils.js';

import { assertNever, isUuid } from '@/helpers/utils';
import {
  findPolygonOverlap,
  makeOffsetPoly,
  polygonCentroid,
} from '@/modules/common/helpers/polygon';
import { mod } from '@/modules/common/helpers/math';
import {
  Area,
  ConnectionRelation,
  ConnectionRelationOrientation,
  ConnectionRelationType,
  HighwayConnectionSide,
  RectangleSpatialArea,
  SegmentSpatialArea,
  SpatialArea2D,
} from '@/modules/floorplanDesign/types';
import {
  areasToFlatCollection,
  SpatialArea2DToPolygon,
} from '@/modules/floorplanDesign/helpers/helpers';
import {
  isHighwayArea,
  isManualArea,
  isRackingArea,
  isSingularStationArea,
  isStationaryArea,
} from '@/modules/floorplanDesign/helpers/typeguards';

import {
  buildHighwayToHighwayCrossing,
  buildHighwayToHighwayCounterFlow,
  buildHighwayToHighwayCoFlow,
  buildHighwayToRackingInsideParallel,
  buildHighwayToOtherAreaParallel,
  buildHighwayToOtherAreaCrossing,
  buildStationaryExtractionToManual,
  buildManualToStationaryInsertion,
  ConnectNeighbors,
  getRackingCategory,
} from './factories/connection';

type AreaDimensions = Pick<RectangleSpatialArea['data'], 'length' | 'width'>;

//
// Angle thresholds
//

/**
 * Angle difference (0–360°) below which two highway segments are considered co-flow.
 * Also used as the mod-180 threshold for area to segment parallelism.
 */
const PARALLEL_THRESHOLD_DEG = 20;

/**
 * Lower bound of the counter-flow band (~160°).
 * Segment pairs with an angle difference inside [160, 200] are counter-flow.
 */
const COUNTER_FLOW_LOWER_DEG = 160;

/**
 * Upper bound of the counter-flow band (~200°).
 */
const COUNTER_FLOW_UPPER_DEG = 200;

//
// Orientation helpers
//

/**
 * Returns the spatial angle in degrees [0, 360) for a rectangle or segment.
 */
const getSpatialAngle = (spatial: SpatialArea2D): number => {
  const angle =
    spatial.type === 'rectangle'
      ? spatial.data.angle * RAD2DEG
      : RAD2DEG *
        Math.atan2(
          spatial.data.end.y - spatial.data.start.y,
          spatial.data.end.x - spatial.data.start.x,
        );
  return mod(angle, 360);
};

/**
 * Returns the orientation between two highway segments.
 *   co_flow      – angle difference < PARALLEL_THRESHOLD_DEG
 *   counter_flow – angle difference in [COUNTER_FLOW_LOWER_DEG, COUNTER_FLOW_UPPER_DEG]
 *   crossing     – everything else
 */
const getSegmentToSegmentOrientation = (
  segA: SpatialArea2D,
  segB: SpatialArea2D,
): 'co_flow' | 'counter_flow' | 'crossing' => {
  const angleA = getSpatialAngle(segA);
  const angleB = getSpatialAngle(segB);
  const diff = mod(angleA - angleB, 360);

  if (diff < PARALLEL_THRESHOLD_DEG || diff > 360 - PARALLEL_THRESHOLD_DEG) {
    return 'co_flow';
  }

  if (diff > COUNTER_FLOW_LOWER_DEG && diff < COUNTER_FLOW_UPPER_DEG) {
    return 'counter_flow';
  }

  return 'crossing';
};

/**
 * Determines whether a highway runs along a rectangle's physical long or short side.
 * A rectangle's angle describes its length axis, but width can still be the larger dimension.
 */
const getAreaConnectionSide = (
  segmentSpatial: SegmentSpatialArea,
  areaSpatial: RectangleSpatialArea,
): HighwayConnectionSide => {
  const segmentAngle = mod(getSpatialAngle(segmentSpatial), 180);
  const areaAngle = mod(getSpatialAngle(areaSpatial), 180);
  let angleDifference = Math.abs(segmentAngle - areaAngle);

  if (angleDifference > 90) angleDifference = 180 - angleDifference;

  const { length, width } = areaSpatial.data;
  const highwayFollowsLengthAxis = angleDifference < PARALLEL_THRESHOLD_DEG;
  const lengthIsTheLongDimension = length >= width;
  const highwayRunsAlongLongSide = highwayFollowsLengthAxis === lengthIsTheLongDimension;

  if (highwayRunsAlongLongSide) return 'long_side';

  return 'short_side';
};

/**
 * Converts a highway segment to its spatial representation.
 */
const segmentToSpatialArea = (segment: Segment): SpatialArea2D => ({
  id: segment.id,
  type: 'segment',
  data: {
    start: segment.start,
    end: segment.end,
    leftSideWidth: segment.leftSideWidth,
    rightSideWidth: segment.rightSideWidth,
  },
});

/**
 * Converts a spatial item to a connection relation endpoint.
 */
const spatialAreaToConnectionEndpoint = (
  spatialArea: SpatialArea2D,
): ConnectionRelation['areaA'] => ({
  id: spatialArea.id,
  type: spatialArea.type === 'segment' ? 'segment' : 'area',
});

/**
 * Determines the connection orientation between two spatial items.
 */
const getConnectionRelationOrientation = (
  spatialAreaA: SpatialArea2D,
  spatialAreaB: SpatialArea2D,
): ConnectionRelationOrientation => {
  if (spatialAreaA.type === 'segment') {
    if (spatialAreaB.type === 'segment') {
      return getSegmentToSegmentOrientation(spatialAreaA, spatialAreaB);
    }

    return getAreaConnectionSide(spatialAreaA, spatialAreaB);
  }

  if (spatialAreaB.type === 'segment') {
    return getAreaConnectionSide(spatialAreaB, spatialAreaA);
  }

  // Orientation is not used for area-to-area connections (cases 7 / 7.5).
  return 'long_side';
};

/**
 * Creates a connection relation between two overlapping spatial items.
 */
const createConnectionRelation = (
  spatialAreaA: SpatialArea2D,
  spatialAreaB: SpatialArea2D,
): ConnectionRelation => ({
  areaA: spatialAreaToConnectionEndpoint(spatialAreaA),
  areaB: spatialAreaToConnectionEndpoint(spatialAreaB),
  orientation: getConnectionRelationOrientation(spatialAreaA, spatialAreaB),
});

//
// Interface
//
interface IInterAreaConnectionUtils {
  collision_tolerance_in_meter: 0.03;

  /**
   * Filter out the candidates that have a spatial overlap with the source entity.
   */
  findPotentialTargets(
    source: SpatialArea2D,
    candidates: SpatialArea2D[],
  ): {
    target: SpatialArea2D;
    point: { x: number; y: number };
  }[];

  /**
   * Find potential connections between 2 areas and return them as connection relations.
   */
  findPotentialConnectionRelationsBetween2Areas(
    area1: Area,
    fromId: string,
    area2: Area,
    collidingId: string,
  ): ConnectionRelation[];

  /**
   * Determines the relation type between two connection ends.
   */
  determineRelationType(
    typeA: 'segment' | 'area',
    typeB: 'segment' | 'area',
  ): ConnectionRelationType;

  /**
   * Filters out connections unrelated to the given area IDs.
   */
  filterRelated(areaIds: string[], connections: InterAreaConnection[]): InterAreaConnection[];

  /**
   * Attempts to find the overlap between two polygons (with collision tolerance)
   * and returns the centroid of the overlap if found.
   */
  findOverlapCenter(
    polyA: { x: number; y: number }[],
    polyB: { x: number; y: number }[],
  ): { x: number; y: number } | null;

  /**
   * Determines if two areas can have an auto-detected connection (geometry-based).
   * Returns false for area pairs that are only connected via the explicit distant tool (case 3).
   */
  isConnectionValid(areaA: Area, areaB: Area): boolean;

  /**
   * Returns true if all connections in an InterAreaConnection are of type CROSSING.
   */
  isCrossingConnection(interAreaConnection: InterAreaConnection): boolean;

  /**
   * Returns the midpoints of the two sides where a highway can connect to the area,
   * in the area's local frame (meters, x along length, y along width).
   * Returns an empty array for highways and areas without an allowed connection side.
   * Pass `dimensions` (meters) to use a live size, for while the area is being scaled.
   */
  getConnectionPoints(area: Area, dimensions?: AreaDimensions): { x: number; y: number }[];

  /**
   * Returns connection updates for areas that have changed position or shape.
   */
  determineConnectionUpdates(
    areaUpdates: Areas,
    fpDesign: Design,
    collisionState: Map<string, string[]>,
  ): {
    connectionsToAdd: InterAreaConnection[];
    connectionsToSwitch: InterAreaConnection[];
    connectionsToRemove: InterAreaConnection[];
  };
}

//
// Implementation
//
export const InterAreaConnectionUtils: IInterAreaConnectionUtils = {
  collision_tolerance_in_meter: 0.03,

  findPotentialTargets(source, candidates) {
    return candidates
      .filter((item) => source.id !== item.id)
      .flatMap((item) => {
        const point = InterAreaConnectionUtils.findOverlapCenter(
          SpatialArea2DToPolygon(source),
          SpatialArea2DToPolygon(item),
        );
        if (!point) return [];
        return [{ target: item, point }];
      });
  },

  findPotentialConnectionRelationsBetween2Areas(area1, fromId, area2, collidingId) {
    let spatial1: SpatialArea2D;
    let spatial2: SpatialArea2D;

    if (isHighwayArea(area1)) {
      const segment = area1.segments.find((s) => s.id === fromId);
      if (!segment) return [];
      spatial1 = segmentToSpatialArea(segment);
    } else {
      spatial1 = { id: area1.id, type: 'rectangle', data: area1.rectangle };
    }

    if (isHighwayArea(area2)) {
      const segment = area2.segments.find((s) => s.id === collidingId);
      if (!segment) return [];
      spatial2 = segmentToSpatialArea(segment);
    } else {
      spatial2 = { id: area2.id, type: 'rectangle', data: area2.rectangle };
    }

    return [createConnectionRelation(spatial1, spatial2)];
  },

  determineRelationType(typeA, typeB) {
    const combo = `${typeA}_to_${typeB}` as `${'area' | 'segment'}_to_${'area' | 'segment'}`;

    switch (combo) {
      case 'segment_to_segment':
        return 'segmentA_to_segmentB';
      case 'area_to_segment':
        return 'areaA_to_segmentB';
      case 'segment_to_area':
        return 'segmentA_to_areaB';
      case 'area_to_area':
        return 'areaA_to_areaB';
      default:
        return combo;
    }
  },

  filterRelated(areaIds, connections) {
    const ids = new Set(areaIds);
    return connections.filter((item) => ids.has(item.idA) || ids.has(item.idB));
  },

  findOverlapCenter(polyA, polyB) {
    const polyWithMarginA = makeOffsetPoly(
      polyA.map((p) => new Vector2(p.x, p.y)),
      InterAreaConnectionUtils.collision_tolerance_in_meter,
    );

    const polyWithMarginB = makeOffsetPoly(
      polyB.map((p) => new Vector2(p.x, p.y)),
      InterAreaConnectionUtils.collision_tolerance_in_meter,
    );

    const intersectionPolygon = findPolygonOverlap(polyWithMarginA, polyWithMarginB);

    if (!intersectionPolygon) return null;

    return polygonCentroid(intersectionPolygon);
  },

  isConnectionValid(areaA, areaB) {
    if (areaA.id === areaB.id) return false;

    const isHighwayA = isHighwayArea(areaA);
    const isHighwayB = isHighwayArea(areaB);
    const isManualA = isManualArea(areaA);
    const isManualB = isManualArea(areaB);

    // Highway auto-connects only to supported non-manual areas.
    if (isHighwayA && isHighwayB) return true;
    if (isHighwayA && !isManualB) return isSupportedHighwayConnectionArea(areaB);
    if (isHighwayB && !isManualA) return isSupportedHighwayConnectionArea(areaA);

    // STATIONARY (EXTRACTION or INSERTION) auto-connects to MANUAL (cases 7 / 7.5)
    if (isManualA && isStationaryArea(areaB)) {
      return areaB.stationaryType === 'EXTRACTION' || areaB.stationaryType === 'INSERTION';
    }
    if (isManualB && isStationaryArea(areaA)) {
      return areaA.stationaryType === 'EXTRACTION' || areaA.stationaryType === 'INSERTION';
    }

    // All other non-highway pairs (RACKING to RACKING, SINGULAR to STATIONARY, etc.)
    // are only connected via the explicit distant-connection tool (case 3) — not auto-detected.
    return false;
  },

  isCrossingConnection(interAreaConnection) {
    return (
      interAreaConnection.aToBConnections.every((c) => c.connectionType === 'CROSSING') &&
      interAreaConnection.bToAConnections.every((c) => c.connectionType === 'CROSSING')
    );
  },

  getConnectionPoints(area, dimensions) {
    if (isHighwayArea(area)) return [];

    const connectionSide = getAllowedConnectionSideForArea(area);
    if (!connectionSide) return [];

    const { length, width } = dimensions ?? area.rectangle;

    if (connectsOnLengthEdges(area, connectionSide, { length, width })) {
      return [
        { x: 0, y: width / 2 },
        { x: 0, y: -width / 2 },
      ];
    }

    return [
      { x: length / 2, y: 0 },
      { x: -length / 2, y: 0 },
    ];
  },

  determineConnectionUpdates(areaUpdates, fpDesign, collisionState) {
    const {
      interAreaConnections: allCurrentConnections,
      areas: allCurrentAreas,
      templates,
    } = fpDesign;

    // Lassie/LassieMini template areas are excluded from automatic connection logic.
    // TPS manages their connections explicitly via interAreaConnections
    // in addTemplate / regenerateTemplate, preventing auto-create/remove
    // behavior in either direction.
    // Shuttle rack areas are intentionally NOT excluded — they participate in
    // the same auto-connection logic as regular racking areas.
    const templateAreaIds = new Set(
      [...templates.lassies, ...templates.lassieMinis].flatMap(
        (template) => template.group.areaIds,
      ),
    );

    const allAreasFlat = [
      ...areasToFlatCollection(allCurrentAreas).filter((area) => !templateAreaIds.has(area.id)),
    ];
    const allAreasById = new Map(allAreasFlat.map((area) => [area.id, area]));

    const connectionsToAdd: InterAreaConnection[] = [];
    const connectionsToSwitch: InterAreaConnection[] = [];
    const connectionsToRemove: InterAreaConnection[] = [];

    areasToFlatCollection(areaUpdates)
      .filter((area) => !templateAreaIds.has(area.id))
      .forEach((fromArea) => {
        const collisionUpdates = isHighwayArea(fromArea)
          ? fromArea.segments.map((s) => s.id)
          : [fromArea.id];

        collisionUpdates.forEach((fromId) => {
          const newCons: InterAreaConnection[] = [];
          const switchedCons: InterAreaConnection[] = [];
          const redundantCons: InterAreaConnection[] = [];

          const existingCons = InterAreaConnectionUtils.filterRelated(
            [fromId],
            allCurrentConnections,
          );

          const currentCollisions = collisionState.get(fromId) || [];

          // Auto-connections that are no longer detected geometrically → remove
          existingCons.forEach((existingCon) => {
            if (existingCon.createdExplicitly) return;
            const stillDetected = currentCollisions.some(
              (r) =>
                (fromId === existingCon.idA && r === existingCon.idB) ||
                (fromId === existingCon.idB && r === existingCon.idA),
            );
            if (!stillDetected) redundantCons.push(existingCon);
          });

          // Detect which relations currently exist geometrically
          const detectedRelations = findPrimaryConnectionRelations(
            fromArea,
            fromId,
            allAreasFlat.filter((area) =>
              isHighwayArea(area)
                ? area.segments.map((s) => s.id).some((id) => currentCollisions.includes(id))
                : currentCollisions.includes(area.id),
            ),
            currentCollisions,
          );

          // Process each detected relation — add new connections, but never change existing configuration
          detectedRelations.forEach((relation) => {
            const { id: areaAId, type: areaAType } = relation.areaA;
            const { id: areaBId, type: areaBType } = relation.areaB;

            const relationType = InterAreaConnectionUtils.determineRelationType(
              areaAType,
              areaBType,
            );

            const pendingCons = [...allCurrentConnections, ...connectionsToAdd, ...newCons];
            const existing = pendingCons.find(
              (c) =>
                (c.idA === areaAId && c.idB === areaBId) ||
                (c.idA === areaBId && c.idB === areaAId),
            );

            let newConnection: InterAreaConnection | undefined;

            switch (relationType) {
              case 'segmentA_to_segmentB':
                newConnection = processHighwayToHighway(relation, existing);
                break;
              case 'areaA_to_segmentB':
              case 'segmentA_to_areaB':
                newConnection = processHighwayToArea(
                  relation,
                  relationType,
                  existing,
                  allAreasById,
                );
                break;
              case 'areaA_to_areaB':
                newConnection = processAreaToArea(relation, existing, allAreasById);
                break;
              default:
                break;
            }

            if (newConnection) newCons.push(newConnection);
          });

          connectionsToAdd.push(...newCons);
          connectionsToSwitch.push(...switchedCons);
          connectionsToRemove.push(...redundantCons);
        });
      });

    return { connectionsToAdd, connectionsToSwitch, connectionsToRemove };
  },
} as const;

//
// Private helpers
//

function isSupportedHighwayConnectionArea(area: Area): boolean {
  return isSingularStationArea(area) || isRackingArea(area) || isStationaryArea(area);
}

function getAllowedConnectionSideForArea(area: Area): HighwayConnectionSide | undefined {
  if (isSingularStationArea(area) || isStationaryArea(area)) {
    const { stacking } = area.constellationSpec;

    if (stacking.startsWith('HORIZONTAL_')) return 'long_side';
    if (stacking.startsWith('VERTICAL_')) return 'short_side';

    return undefined;
  }

  if (!isRackingArea(area)) return undefined;

  switch (area.rackType) {
    case 'ONE_SIDED_INSIDE':
    case 'SHUTTLE':
      return 'long_side';
    case 'ONE_SIDED_ALONGSIDE':
    case 'TWO_SIDED_ALONGSIDE':
      return 'short_side';
    default:
      return assertNever(area.rackType);
  }
}

/**
 * Whether the connection points lie on the edges along the rectangle's length axis.
 * - Singular/stationary: The stacking determines this, and as that's automatically updates, it should always be long side
 * - Racking: the side comes from the rack type and means the physically longer/shorter
 *   edges, so it flips when the area is scaled past square.
 */
function connectsOnLengthEdges(
  area: Area,
  connectionSide: HighwayConnectionSide,
  { length, width }: AreaDimensions,
): boolean {
  if (isRackingArea(area)) {
    const lengthEdgesSide: HighwayConnectionSide = length >= width ? 'long_side' : 'short_side';
    return connectionSide === lengthEdgesSide;
  }

  return connectionSide === 'long_side';
}

/**
 * Finds the primary connection relations for an area.
 * Highway-to-area relations are retained only when they touch the side allowed by the area type.
 */
function findPrimaryConnectionRelations(
  fromArea: Area,
  fromId: string,
  allAreas: Area[],
  collidingItems: string[],
): ConnectionRelation[] {
  return collidingItems.flatMap((collidingId) => {
    const otherArea = isUuid(collidingId)
      ? allAreas.find((area) => area.id === collidingId)
      : allAreas.find(
          (area) => isHighwayArea(area) && area.segments.map((s) => s.id).includes(collidingId),
        );

    if (!otherArea || !InterAreaConnectionUtils.isConnectionValid(fromArea, otherArea)) return [];

    const isHighwayFrom = isHighwayArea(fromArea);
    const isHighwayOther = isHighwayArea(otherArea);

    const potentialRelations =
      InterAreaConnectionUtils.findPotentialConnectionRelationsBetween2Areas(
        fromArea,
        fromId,
        otherArea,
        collidingId,
      );

    if (isHighwayFrom && isHighwayOther) return potentialRelations;

    // The only valid non-highway pair is MANUAL to INSERTION / EXTRACTION.
    if (!isHighwayFrom && !isHighwayOther) return potentialRelations;

    const nonHighwayArea = isHighwayFrom ? otherArea : fromArea;
    const connectionSide = getAllowedConnectionSideForArea(nonHighwayArea);

    if (!connectionSide) return [];

    return potentialRelations.filter(({ orientation }) => orientation === connectionSide);
  });
}

function isHighwayToHighwayOrientation(
  orientation: ConnectionRelationOrientation,
): orientation is 'co_flow' | 'counter_flow' | 'crossing' {
  switch (orientation) {
    case 'co_flow':
    case 'counter_flow':
    case 'crossing':
      return true;
    case 'long_side':
    case 'short_side':
      return false;
    default:
      return assertNever(orientation);
  }
}

function isHighwayToAreaOrientation(
  orientation: ConnectionRelationOrientation,
): orientation is 'long_side' | 'short_side' {
  switch (orientation) {
    case 'long_side':
    case 'short_side':
      return true;
    case 'co_flow':
    case 'counter_flow':
    case 'crossing':
      return false;
    default:
      return assertNever(orientation);
  }
}

function buildHighwayToHighwayByOrientation(
  orientation: 'co_flow' | 'counter_flow' | 'crossing',
  segmentIdA: string,
  segmentIdB: string,
): InterAreaConnection {
  switch (orientation) {
    case 'crossing':
      return buildHighwayToHighwayCrossing(segmentIdA, segmentIdB);
    case 'counter_flow':
      return buildHighwayToHighwayCounterFlow(segmentIdA, segmentIdB);
    case 'co_flow':
      return buildHighwayToHighwayCoFlow(segmentIdA, segmentIdB);
    default:
      return assertNever(orientation);
  }
}

/**
 * Highway-to-area CROSSING is only valid for alongside racking areas.
 * Singular, stationary, manual, and inside/shuttle racking areas must never
 * become CROSSING from the highway-to-area angle threshold.
 */
function canBuildHighwayToAreaCrossing(area: Area): boolean {
  if (!isRackingArea(area)) return false;

  switch (area.rackType) {
    case 'ONE_SIDED_ALONGSIDE':
    case 'TWO_SIDED_ALONGSIDE':
      return true;
    case 'ONE_SIDED_INSIDE':
    case 'SHUTTLE':
      return false;
    default:
      return assertNever(area.rackType);
  }
}

function buildHighwayToAreaConnection(
  isRackingInside: boolean,
  shouldCreateCrossing: boolean,
  areaId: string,
  segmentId: string,
  connectNeighbors: ConnectNeighbors,
): InterAreaConnection {
  if (shouldCreateCrossing) {
    return buildHighwayToOtherAreaCrossing(areaId, segmentId);
  }

  if (isRackingInside) {
    return buildHighwayToRackingInsideParallel(areaId, segmentId, connectNeighbors);
  }

  return buildHighwayToOtherAreaParallel(areaId, segmentId, connectNeighbors);
}

/** Highway segment to highway segment — create based on angle orientation, but never switch existing configuration. */
function processHighwayToHighway(
  relation: ConnectionRelation,
  existing: InterAreaConnection | undefined,
): InterAreaConnection | undefined {
  if (existing) return undefined;
  if (!isHighwayToHighwayOrientation(relation.orientation)) return undefined;

  return buildHighwayToHighwayByOrientation(
    relation.orientation,
    relation.areaA.id,
    relation.areaB.id,
  );
}

/** Highway segment to non-highway area — create based on orientation, but never switch existing configuration. */
function processHighwayToArea(
  relation: ConnectionRelation,
  relationType: 'areaA_to_segmentB' | 'segmentA_to_areaB',
  existing: InterAreaConnection | undefined,
  allAreasById: Map<string, Area>,
): InterAreaConnection | undefined {
  if (existing) return undefined;

  const areaId = relationType === 'areaA_to_segmentB' ? relation.areaA.id : relation.areaB.id;
  const segmentId = relationType === 'areaA_to_segmentB' ? relation.areaB.id : relation.areaA.id;

  const nonHighwayArea = allAreasById.get(areaId);
  if (!nonHighwayArea) return undefined;
  if (!isHighwayToAreaOrientation(relation.orientation)) return undefined;

  const connectNeighbors: ConnectNeighbors =
    'connectNeighbors' in nonHighwayArea ? nonHighwayArea.connectNeighbors : 'NO_NEIGHBOR_MAPPING';

  const isRackingInside =
    isRackingArea(nonHighwayArea) &&
    getRackingCategory(nonHighwayArea.rackType) === 'RACKING_INSIDE';

  const shouldCreateCrossing =
    relation.orientation === 'short_side' && canBuildHighwayToAreaCrossing(nonHighwayArea);

  return buildHighwayToAreaConnection(
    isRackingInside,
    shouldCreateCrossing,
    areaId,
    segmentId,
    connectNeighbors,
  );
}

/** Stationary to manual — one-way logical connections for insertion and extraction areas. */
function processAreaToArea(
  relation: ConnectionRelation,
  existing: InterAreaConnection | undefined,
  allAreasById: Map<string, Area>,
): InterAreaConnection | undefined {
  if (existing) return undefined;

  const areaA = allAreasById.get(relation.areaA.id);
  const areaB = allAreasById.get(relation.areaB.id);
  if (!areaA || !areaB) return undefined;

  // Normalise so stationary is always first, manual always second
  const [stationary, manual] = isStationaryArea(areaA) ? [areaA, areaB] : [areaB, areaA];
  const [stationaryId, manualId] = isStationaryArea(areaA)
    ? [relation.areaA.id, relation.areaB.id]
    : [relation.areaB.id, relation.areaA.id];

  if (!isStationaryArea(stationary) || !isManualArea(manual)) return undefined;

  switch (stationary.stationaryType) {
    case 'EXTRACTION':
      return buildStationaryExtractionToManual(stationaryId, manualId);
    case 'INSERTION':
      return buildManualToStationaryInsertion(manualId, stationaryId);
    case 'PARKING':
    case 'CHARGING':
      // PARKING / CHARGING stationary areas do not connect to manual areas
      return undefined;
    default:
      return undefined;
  }
}
